import { AntInput } from "@/CommonElements/AntInput";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import { ActionsFooter } from "@/Component/ActionsFooter";
import {
    useCreateJobSectorMutation,
    useGetOneJobSectorQuery,
    useUpdateJobSectorMutation,
} from "@/services/basicData/basicData";
import { MODES } from "@/utils/constants";
import { cn, handleFormErrors } from "@/utils/helpers";
import { Switch, Tag } from "antd";
import { useEffect } from "react";
import { Button, Stack } from "react-bootstrap";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Container, Spinner } from "reactstrap";
import { validateArabicAndNumbersOnly, validateEnglishAndNumbersOnly } from "../../ProfileForm";

export const JobSectorsForm = () => {
    const { t } = useTranslation();
    const { id, action } = useParams();
    const resolvedAction = action ?? (!id ? "create" : "edit");

    const title = {
        create: t("CREATE_JOB"),
        edit: t("EDIT_JOB_SECTOR"),
        view: t("VIEW_JOB_SECTOR"),
    };

    return (
        <Stack gap={2}>
            <PageHeader title={title[resolvedAction] || title.create} />
            <Container fluid>
                <JobSectorsFormCRUD />
            </Container>
        </Stack>
    );
};

const getJobSectorsBackLink = () => {
    if (typeof window === "undefined" || window.history.length > 1) return -1;
    return window.location.pathname.includes("seasonal_hr")
        ? "/app/seasonal_hr/basic-data/job-sectors"
        : "/app/makkah/basic-data/job-sectors";
};

const JobSectorsFormCRUD = () => {
    const { t } = useTranslation();
    const { id, action } = useParams();
    const resolvedAction = action ?? (!id ? "create" : "edit");
    const isView = resolvedAction === MODES.view;
    const isUpdate = resolvedAction === MODES.edit;


    const { data: jobSectorResponse, isLoading } = useGetOneJobSectorQuery({ id });

    const { mutate: createJobSector, isPending: isCreatePending } =
        useCreateJobSectorMutation();
    const { mutate: updateJobSector, isPending: isUpdatePending } =
        useUpdateJobSectorMutation();


    const form = useForm({
        disabled: isLoading || isCreatePending || isUpdatePending,
        defaultValues: {
            name_ar: "",
            name_en: "",
            is_enabled_for_makkah: true,
            is_enabled_for_madina: true,
        },
    });
    const { control, handleSubmit, setError, formState: { errors } } = form;
    const jobSector = jobSectorResponse?.data?.data;
    useEffect(() => {
        if (jobSector) {
            form.reset({
                name_ar: jobSector.name_ar ?? "",
                name_en: jobSector.name_en ?? "",
                is_enabled_for_makkah: !!jobSector.is_enabled_for_makkah,
                is_enabled_for_madina: !!jobSector.is_enabled_for_madina,
            });
        }
    }, [jobSector]);

    const onSubmit = (data) => {
        if (isUpdate) {
            updateJobSector({ ...data, id }, {
                onError: (error) => {
                    handleFormErrors(error, setError, t, "UPDATE_JOB_SECTOR_FAILED");
                },
            });
        } else {
            createJobSector(data, {
                onError: (error) => {
                    handleFormErrors(error, setError, t, "UPDATE_JOB_SECTOR_FAILED");
                },
            });
        }
    };

    const isPending = isCreatePending || isUpdatePending;

    const controllers = [
        {
            name: "name_ar",
            labelProps: { children: t("NAME_AR"), className: "required" },
            controllerProps: {
                name: "name_ar",
                rules: {
                    required: t("FIELD_REQUIRED"),
                    validate: (value) => validateArabicAndNumbersOnly(value, t),
                },
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        placeholder={t("NAME_AR")}
                        status={invalid ? "error" : undefined}
                    />
                ),
            },
            layout: { colProps: { xs: 12, md: 6 } },
            renderValue: (value) => value || "-",
        },
        {
            name: "name_en",
            labelProps: { children: t("NAME_EN"), className: "required" },
            controllerProps: {
                name: "name_en",
                rules: {
                    required: t("FIELD_REQUIRED"),
                    validate: (value) => validateEnglishAndNumbersOnly(value, t),
                },
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        placeholder={t("NAME_EN")}
                        status={invalid ? "error" : undefined}
                    />
                ),
            },
            layout: { colProps: { xs: 12, md: 6 } },
            renderValue: (value) => value || "-",
        },
        {
            name: "is_enabled_for_makkah",
            labelProps: { children: t("ENABLED_FOR_MAKKAH") },
            controllerProps: {
                name: "is_enabled_for_makkah",
                render: ({ field }) => (
                    <div>
                        <Switch
                            {...field}
                            checked={!!field.value}
                            onChange={(checked) => field.onChange(checked)}
                        />
                    </div>
                ),
            },
            layout: { colProps: { xs: 12, md: 6 } },
            renderValue: (value) => (
                <Tag color={!!value ? "green" : "red"}>
                    {!!value ? t("ENABLED") : t("DISABLED")}
                </Tag>
            ),
        },
        {
            name: "is_enabled_for_madina",
            labelProps: { children: t("ENABLED_FOR_MADINA") },
            controllerProps: {
                name: "is_enabled_for_madina",
                render: ({ field }) => (
                    <div>
                        <Switch
                            {...field}
                            checked={!!field.value}
                            onChange={(checked) => field.onChange(checked)}
                        />
                    </div>
                ),
            },
            layout: { colProps: { xs: 12, md: 6 } },
            renderValue: (value) => (
                <Tag color={!!value ? "green" : "red"}>
                    {!!value ? t("ENABLED") : t("DISABLED")}
                </Tag>
            ),
        },
    ];

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Controllers
                    isLoading={!!id && isLoading}
                    isViewMode={isView}
                    watch={form.watch}
                    controllers={controllers.map((config) => ({
                        ...config,
                        labelProps: {
                            ...config.labelProps,
                            className: cn("fw-bold", config.labelProps?.className),
                        },
                        controllerProps: {
                            ...config.controllerProps,
                            control,
                            errors,
                        },
                    }))}
                />
                {!isView && (
                    <ActionsFooter>
                        <Button
                            className="btn btn-primary"
                            type="submit"
                            disabled={isPending}
                        >
                            {isPending && <Spinner size="sm" />}
                            {isUpdate ? t("UPDATE") : t("CREATE")}
                        </Button>
                        <Link
                            to={getJobSectorsBackLink}
                            className="btn btn-danger"
                            type="button"
                            disabled={isPending}
                            style={{ pointerEvents: isPending ? "none" : "auto" }}
                        >
                            {t("CANCEL")}
                        </Link>
                    </ActionsFooter>
                )}
            </form>
        </FormProvider>
    );
};
